import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown,
  Icon,
} from '@resy/ui-kitchen';
import classNames from 'classnames';
import pinSVG from '../../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';
import calendarSVG from '../../../../../../ui-kitchen/src/svg/icons/ic_calendar-solid_20x20.svg';
import guestSVG from '../../../../../../ui-kitchen/src/svg/icons/ic_guests_20x20.svg';
import statusSVG from '../../../../../../ui-kitchen/src/svg/icons/knife-fork.svg';
import {
  handleMktAnalytics,
  getMonthOptions,
  getCityOptions,
} from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import { UPCOMING_EVENTS_REFRESH, VENUE_OPTIONS } from '../constants';
import './AmexPlatinumNightsSelector.scss';

const AmexPlatinumNightsSelector = (
  {
    Analytics,
    locations,
    venuesMonths,
    handleFilterValue,
    filterValueInComp,
    filteringHelp,
    disableFiltersConfig,
  }
) => {
  let areSecondaryFiltersDisabled = !!filterValueInComp?.disablingFilters;
  if (filterValueInComp?.slug !== '' && filterValueInComp?.slug !== undefined) {
    areSecondaryFiltersDisabled = !!filterValueInComp?.disablingFilters && disableFiltersConfig;
  }

  const MONTH_OPTIONS_NEW = [{
    key: '',
    value: '',
    orderId: 0,
    label: UPCOMING_EVENTS_REFRESH,
  }];

  const [selectedGuest, setSelectedGuest] = useState(filterValueInComp?.size || '2');
  const [isDropdownClicked, setIsDropdownClicked] = useState(false);

  const handleCityDropdown = (cityObj) => {
    const extraPrams = {
      action_name: 'Dropdown Click',
      item_name: 'City',
    };

    const selectedLocation = locations?.find((locationItem) => locationItem?.url_slug === cityObj?.city);
    const filterValue = {
      month: filterValueInComp?.month,
      slug: cityObj?.city,
      name: selectedLocation?.name || '',
      code: selectedLocation?.code || '',
      size: selectedGuest,
      monthFilter: filterValueInComp?.monthFilter,
      status: filterValueInComp?.status,
      disablingFilters: selectedLocation?.data === false,
    };
    handleFilterValue(filterValue);
    if (isDropdownClicked) handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
  };

  const handleMonthDropdown = (monthObj) => {
    if (areSecondaryFiltersDisabled) return;
    const extraPrams = {
      action_name: 'Dropdown Click',
      item_name: 'Month',
    };

    const filterValue = {
      month: monthObj?.month - 1,
      slug: filterValueInComp?.slug,
      name: filterValueInComp?.name || '',
      code: filterValueInComp?.code || '',
      size: selectedGuest,
      monthFilter: monthObj?.month,
      status: filterValueInComp?.status,
      disablingFilters: filterValueInComp?.disablingFilters,
    };

    handleFilterValue(filterValue);
    if (isDropdownClicked) handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
  };

  const handleVenuesDropdown = (venueObj) => {
    if (areSecondaryFiltersDisabled) return;
    const extraPrams = {
      action_name: 'Dropdown Click',
      item_name: 'Status',
    };

    const filterValue = {
      month: filterValueInComp?.month,
      slug: filterValueInComp?.slug,
      name: filterValueInComp?.name || '',
      code: filterValueInComp?.code || '',
      size: selectedGuest,
      monthFilter: filterValueInComp?.monthFilter,
      status: venueObj?.status,
      disablingFilters: filterValueInComp?.disablingFilters,
    };

    handleFilterValue(filterValue);
    if (isDropdownClicked) handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
  };

  const getOptions = (maxGuests) => {
    const ONE = '1';
    const options = [{ value: ONE, key: ONE, label: '1 Guest' }];
    for (let n = 2; n <= maxGuests; n += 1) {
      const NUMBER = `${n}`;
      const label = `${n} Guests`;
      options.push({ value: NUMBER, key: NUMBER, label });
    }
    return options;
  };

  const handleGuestDropdown = (guestObj) => {
    if (areSecondaryFiltersDisabled) return;
    if (guestObj?.guest === selectedGuest) return;

    const extraPrams = {
      action_name: 'Dropdown Click',
      item_name: 'Party Size',
    };

    const filterValue = {
      month: filterValueInComp?.month,
      slug: filterValueInComp?.slug,
      name: filterValueInComp?.name || '',
      code: filterValueInComp?.code || '',
      size: guestObj?.guest,
      monthFilter: filterValueInComp?.monthFilter,
      status: filterValueInComp?.status,
      disablingFilters: filterValueInComp?.disablingFilters,
    };
    handleFilterValue(filterValue);
    setSelectedGuest(guestObj?.guest);
    if (isDropdownClicked) handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams);
  };

  const handleFormatLabel = (option, type) => {
    let iconComponent = guestSVG;
    if (type === 'calendar') iconComponent = calendarSVG;
    else if (type === 'location') iconComponent = pinSVG;
    else if (type === 'status') iconComponent = statusSVG;

    return (
      <span className="pin">
        <span aria-hidden="true">
          <Icon
            component={iconComponent}
            name={type}
            height="16px"
            width="16px"
          />
        </span>
        <span>{option.label}</span>
      </span>
    );
  };

  const handleUserDropdownClick = () => setIsDropdownClicked(true);

  const selectMonth = () => (
    <Dropdown
      name="month"
      id="monthselect"
      onChange={handleMonthDropdown}
      onClick={handleUserDropdownClick}
      formatLabel={(option) => handleFormatLabel(option, 'calendar')}
      options={getMonthOptions(venuesMonths, MONTH_OPTIONS_NEW)}
      selected={filterValueInComp?.monthFilter}
      value={filterValueInComp?.month}
      ariaLabel="Month"
      disabled={areSecondaryFiltersDisabled}
    />
  );

  const selectCity = () => (
    <Dropdown
      name="city"
      id="cityselect"
      onChange={handleCityDropdown}
      onClick={handleUserDropdownClick}
      options={getCityOptions(locations)}
      formatLabel={(option) => handleFormatLabel(option, 'location')}
      selected={filterValueInComp?.slug}
      value={filterValueInComp?.slug}
      ariaLabel="City"
    />
  );

  const selectVenuesByStatus = () => (
    <Dropdown
      name="status"
      id="statusselect"
      onChange={handleVenuesDropdown}
      onClick={handleUserDropdownClick}
      formatLabel={(option) => handleFormatLabel(option, 'status')}
      options={VENUE_OPTIONS}
      selected={filterValueInComp?.status}
      value={filterValueInComp?.status}
      ariaLabel="Venue status"
      disabled={areSecondaryFiltersDisabled}
    />
  );

  const selectGuest = () => (
    <Dropdown
      name="guest"
      id="guestselect"
      onChange={handleGuestDropdown}
      onClick={handleUserDropdownClick}
      options={getOptions(20)}
      formatLabel={(option) => handleFormatLabel(option, 'guest')}
      selected={selectedGuest}
      value={selectedGuest}
      ariaLabel="guest"
      disabled={areSecondaryFiltersDisabled}
    />
  );

  return (
    <aside aria-label="Venue filters" className="AmexPlatinumNightsSelector">
      <p className="sr-only">{filteringHelp}</p>
      <div className="AmexPlatinumNightsSelector__wrapper">
        <div className="AmexPlatinumNightsSelector__city">
          {selectCity()}
        </div>
        <div className={classNames('AmexPlatinumNightsSelector__month', { 'AmexPlatinumNightsSelector__month--disabled': areSecondaryFiltersDisabled })}>
          {selectMonth()}
        </div>
      </div>
      <div className="AmexPlatinumNightsSelector__wrapper">
        <div className={classNames('AmexPlatinumNightsSelector__status', { 'AmexPlatinumNightsSelector__status--disabled': areSecondaryFiltersDisabled })}>
          {selectVenuesByStatus()}
        </div>
        <div className={classNames('AmexPlatinumNightsSelector__guests', { 'AmexPlatinumNightsSelector__guests--disabled': areSecondaryFiltersDisabled })}>
          {selectGuest()}
        </div>
      </div>
    </aside>
  );
};

export default AmexPlatinumNightsSelector;

AmexPlatinumNightsSelector.propTypes = {
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  locations: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    url_slug: PropTypes.string,
  })).isRequired,
  venuesMonths: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  handleFilterValue: PropTypes.func.isRequired,
  closestCity: PropTypes.shape({ code: PropTypes.string, url_slug: PropTypes.string }).isRequired,
  filterValueInComp: PropTypes.shape({
    slug: PropTypes.string,
    month: PropTypes.number,
    size: PropTypes.string,
    monthFilter: PropTypes.string,
    status: PropTypes.string,
    disablingFilters: PropTypes.bool,
    name: PropTypes.string,
    code: PropTypes.string,
  }).isRequired,
  filteringHelp: PropTypes.string.isRequired,
  disableFiltersConfig: PropTypes.bool,
};

AmexPlatinumNightsSelector.defaultProps = {
  disableFiltersConfig: true,
};
